import React from 'react';
import { motion } from 'framer-motion';
import {
  FiArrowRight,
  FiBarChart2,
  FiLink,
  FiPercent,
  FiShare2,
  FiUsers,
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const affiliateWhatsappUrl = 'https://go.digitalgigs.eu/chat';

const benefits = [
  {
    icon: FiLink,
    title: 'Get Your Affiliate Link',
    description:
      'Receive a trackable affiliate link to share with your audience, network, and business contacts.',
  },
  {
    icon: FiPercent,
    title: 'Earn Commission',
    description:
      'Benefit from successful referrals when people purchase DigitalGigs products and services through your link.',
  },
  {
    icon: FiShare2,
    title: 'Promote Great Offers',
    description:
      'Share practical digital services that help businesses improve their online presence and growth.',
  },
  {
    icon: FiBarChart2,
    title: 'Track Your Results',
    description:
      'Monitor your promotional activity and understand which channels are creating the most interest.',
  },
];

const AffiliateProgram = () => {
  return (
    <main className="bg-white pt-20">
      <section className="relative overflow-hidden bg-slate-950 py-24 text-white lg:py-36">
        <div className="pointer-events-none absolute -right-48 -top-48 h-[600px] w-[600px] rounded-full bg-emerald-500/15 blur-[140px]" />
        <div className="pointer-events-none absolute -bottom-48 -left-48 h-[500px] w-[500px] rounded-full bg-blue-500/10 blur-[140px]" />

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 flex justify-center"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-5 py-2 text-xs font-black uppercase tracking-[0.2em] text-emerald-400">
                <SafeIcon icon={FiUsers} />
                DigitalGigs Affiliate Program
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-8 text-5xl font-extrabold leading-tight tracking-tight md:text-7xl"
            >
              Promote Digital Growth.
              <span className="block text-emerald-400">
                Earn From Your Network.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mx-auto mb-10 max-w-3xl text-xl font-medium leading-relaxed text-slate-400"
            >
              Join the DigitalGigs affiliate program and earn by recommending our
              websites, marketing systems, AI video services, and other digital
              growth solutions to people and businesses that need them.
            </motion.p>

            <motion.a
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              href={affiliateWhatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-full bg-emerald-400 px-10 py-5 text-lg font-bold text-slate-950 shadow-2xl shadow-emerald-400/20 transition-all hover:-translate-y-1 hover:bg-emerald-300"
            >
              Message Us on WhatsApp
              <SafeIcon icon={FiArrowRight} className="ml-3" />
            </motion.a>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <p className="mb-4 text-sm font-black uppercase tracking-[0.2em] text-emerald-600">
              How it works
            </p>

            <h2 className="text-4xl font-extrabold tracking-tight text-slate-900 md:text-5xl">
              Turn Recommendations Into Opportunity
            </h2>

            <p className="mt-6 text-lg font-medium leading-relaxed text-slate-500">
              Whether you are a creator, marketer, consultant, community leader,
              or simply have a strong network, you can introduce people to
              DigitalGigs and benefit from successful referrals.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit, index) => (
              <motion.article
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="rounded-[2rem] border border-slate-200 bg-slate-50 p-8 transition-all hover:-translate-y-1 hover:border-emerald-200 hover:bg-white hover:shadow-xl"
              >
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600">
                  <SafeIcon icon={benefit.icon} className="text-2xl" />
                </div>

                <h3 className="mb-3 text-xl font-bold text-slate-900">
                  {benefit.title}
                </h3>

                <p className="font-medium leading-relaxed text-slate-600">
                  {benefit.description}
                </p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <div className="rounded-[3rem] bg-white p-8 shadow-xl md:p-16">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600">
              <SafeIcon icon={FiLink} className="text-3xl" />
            </div>

            <h2 className="mb-6 text-3xl font-extrabold text-slate-900 md:text-5xl">
              Ready to Start Earning?
            </h2>

            <p className="mx-auto mb-10 max-w-2xl text-lg font-medium leading-relaxed text-slate-500">
              Message us directly on WhatsApp to learn how to join the
              DigitalGigs affiliate network, receive your affiliate link, and
              start promoting our products and services.
            </p>

            <a
              href={affiliateWhatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-2xl bg-emerald-600 px-8 py-4 font-bold text-white shadow-lg shadow-emerald-600/20 transition-all hover:bg-emerald-700"
            >
              Become an Affiliate on WhatsApp
              <SafeIcon icon={FiArrowRight} className="ml-3" />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AffiliateProgram;
